import * as THREE from 'three';
import { chunkSize, worldHeight } from './TerrainConstants';
import { WorldGenerator } from './WorldGenerator';
import { Float32Matrix2D } from '../models/Float32Matrix2D';
import { clamp } from '../helpers/utils';
import VegetationController from './VegetationController';

export interface TerrainLOD {
    readonly gridSize: number;
    readonly textureSize: number;
    readonly distance: number;
}

export const terrainLODs: readonly TerrainLOD[] = [
    { gridSize: 256, textureSize: 256, distance: 512 },
    { gridSize: 128, textureSize: 128, distance: 1024 },
    { gridSize: 64, textureSize: 64, distance: 2048 },
    { gridSize: 32, textureSize: 32, distance: 4096 },
    { gridSize: 16, textureSize: 16, distance: Number.POSITIVE_INFINITY },
];

const LODHysteresis = 0.1;
const skirtDepth = 64;
const baseShadingScale = 100;
const waterVertexShader = `
    attribute float waterMask;

    uniform float uTime;
    uniform float uWaveSpeed;
    uniform float uWaveScale;

    varying vec2 vUv;
    varying float vWaterMask;

    float waveHeight(vec2 p, float time) {
        float waveA = sin(p.x * 1.7 + time * 1.4);
        float waveB = cos(p.y * 1.25 - time * 1.1);
        return (waveA + waveB) * 0.04;
    }

    void main() {
        float time = uTime * uWaveSpeed;
        vec2 wavePosition = position.xz * uWaveScale;
        vec3 transformed = position;
        transformed.y += waveHeight(wavePosition, time);

        vUv = uv;
        vWaterMask = waterMask;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(transformed, 1.0);
    }
`;

const waterFragmentShader = `
    uniform vec3 uWaterColor;
    uniform float uTime;
    uniform float uWaveSpeed;
    uniform float uWaveScale;
    uniform float uOpacity;

    varying vec2 vUv;
    varying float vWaterMask;

    float waveHeight(vec2 p, float time) {
        float waveA = sin(p.x * 1.7 + time * 1.4);
        float waveB = cos(p.y * 1.25 - time * 1.1);
        return (waveA + waveB) * 0.5;
    }

    void main() {
        float mask = vWaterMask;
        if (mask < 0.01) discard;

        float time = uTime * uWaveSpeed;
        vec2 wavePosition = vUv * 256.0 * uWaveScale;
        float ripple = waveHeight(wavePosition, time);
        // The distant water is rendered by MeshStandardMaterial and is darkened
        // by the scene lighting. Match that appearance in the unlit shader.
        vec3 color = uWaterColor * (0.42 + ripple * 0.025);
        gl_FragColor = vec4(color, mask * uOpacity);
    }
`;

export default class ChunkController {
    private readonly scene: THREE.Scene;
    private readonly position: THREE.Vector3;
    private readonly generator: WorldGenerator;
    private mesh: THREE.Mesh | null = null;
    private geometry: THREE.BufferGeometry | null = null;
    private material: THREE.MeshStandardMaterial | null = null;
    private texture: THREE.CanvasTexture | null = null;
    private waterMesh: THREE.Mesh | null = null;
    private waterGeometry: THREE.BufferGeometry | null = null;
    private waterMaterial: THREE.ShaderMaterial | null = null;
    private currentLOD = -1;
    private readonly vegetation: VegetationController;

    constructor(scene: THREE.Scene, generator: WorldGenerator, position: THREE.Vector3) {
        this.scene = scene;
        this.generator = generator;
        this.position = position;
        this.vegetation = new VegetationController(scene, generator, position);
        this.setLOD(terrainLODs.length - 1);

        if (this.geometry === null || this.material === null) {
            throw new Error('Chunk resources were not created');
        }
        this.mesh = new THREE.Mesh(this.geometry, this.material);
        this.mesh.castShadow = true;
        this.mesh.receiveShadow = true;
        this.mesh.position.copy(position);
        this.scene.add(this.mesh);
    }

    public getPosition(): THREE.Vector3 { return this.position; }

    public update(cameraPosition: THREE.Vector3, elapsedTime: number): void {
        const center = new THREE.Vector3(this.position.x + chunkSize / 2, 0, this.position.z + chunkSize / 2);
        const distance = center.distanceTo(cameraPosition);
        this.vegetation.update(distance);
        const nextLOD = this.getNextLOD(distance);
        this.updateWaterUniforms(elapsedTime);
        if (nextLOD === this.currentLOD) return;

        this.setLOD(nextLOD);
        if (this.mesh !== null && this.geometry !== null && this.material !== null) {
            this.mesh.geometry = this.geometry;
            this.mesh.material = this.material;
        }
        this.updateWaterUniforms(elapsedTime);
    }

    public destroy(): void {
        if (this.mesh !== null) {
            this.scene.remove(this.mesh);
            this.mesh = null;
        }
        if (this.waterMesh !== null) {
            this.scene.remove(this.waterMesh);
            this.waterMesh = null;
        }
        this.disposeResources();
        this.vegetation.destroy();
    }

    private getNextLOD(distance: number): number {
        let LOD = this.currentLOD;
        while (LOD > 0 && distance < terrainLODs[LOD - 1].distance * (1 - LODHysteresis)) LOD--;
        while (LOD < terrainLODs.length - 1 && distance > terrainLODs[LOD].distance * (1 + LODHysteresis)) LOD++;
        return LOD;
    }

    private setLOD(LODIndex: number): void {
        const LOD = terrainLODs[LODIndex];
        const heightMap = new Float32Matrix2D(LOD.gridSize, LOD.gridSize);
        const waterMap = new Float32Matrix2D(LOD.gridSize, LOD.gridSize);
        this.generateMaps(heightMap, waterMap);

        const hasDetailedWater = LODIndex <= this.generator.options.water.lodThreshold;
        const nextGeometry = this.createGeometry(heightMap, waterMap, LOD.textureSize, !hasDetailedWater);
        const nextTexture = new THREE.CanvasTexture(this.generateTerrainTexture(
            heightMap,
            waterMap,
            LOD.textureSize,
            LODIndex > this.generator.options.water.lodThreshold,
        ));
        nextTexture.flipY = false;
        nextTexture.wrapS = THREE.ClampToEdgeWrapping;
        nextTexture.wrapT = THREE.ClampToEdgeWrapping;
        nextTexture.colorSpace = THREE.SRGBColorSpace;
        const nextMaterial = new THREE.MeshStandardMaterial({
            map: nextTexture,
            roughness: 1,
            metalness: 0,
            side: THREE.DoubleSide,
            shadowSide: THREE.DoubleSide,
        });
        const nextWater = LODIndex <= this.generator.options.water.lodThreshold
            ? this.createWaterResources(waterMap, LOD.gridSize)
            : null;

        this.disposeResources();
        this.geometry = nextGeometry;
        this.texture = nextTexture;
        this.material = nextMaterial;
        this.waterGeometry = nextWater?.geometry ?? null;
        this.waterMaterial = nextWater?.material ?? null;
        this.currentLOD = LODIndex;

        if (this.waterMesh !== null) {
            this.scene.remove(this.waterMesh);
        }
        this.waterMesh = this.waterGeometry !== null && this.waterMaterial !== null
            ? new THREE.Mesh(this.waterGeometry, this.waterMaterial)
            : null;
        if (this.waterMesh !== null) {
            this.waterMesh.position.set(
                this.position.x + chunkSize / 2,
                this.position.y,
                this.position.z + chunkSize / 2,
            );
            this.waterMesh.renderOrder = 1;
            this.waterMesh.receiveShadow = true;
            this.scene.add(this.waterMesh);
        }
    }

    private disposeResources(): void {
        this.geometry?.dispose();
        this.material?.dispose();
        this.texture?.dispose();
        this.waterGeometry?.dispose();
        this.waterMaterial?.dispose();
        this.geometry = null;
        this.material = null;
        this.texture = null;
        this.waterGeometry = null;
        this.waterMaterial = null;
    }

    private generateMaps(heightMap: Float32Matrix2D, waterMap: Float32Matrix2D): void {
        for (let x = 0; x < heightMap.width; x++) {
            for (let z = 0; z < heightMap.height; z++) {
                const ground = this.generateGroundElevation(x, z, heightMap.width);
                heightMap.set(x, z, ground);
                waterMap.set(x, z, this.generateWaterElevation(ground));
            }
        }
    }

    private generateGroundElevation(x: number, z: number, gridSize: number): number {
        const ratio = chunkSize / (gridSize - 1);
        return this.generator.getWorldHeight(this.position.x + x * ratio, this.position.z + z * ratio);
    }

    private generateWaterElevation(ground: number): number {
        const { waterLevel } = this.generator.options;
        return clamp(waterLevel - ground, 0, waterLevel);
    }

    private getSurfaceElevation(
        heightMap: Float32Matrix2D,
        waterMap: Float32Matrix2D,
        x: number,
        z: number,
        useFilledWaterSurface: boolean,
    ): number {
        const ground = heightMap.get(x, z);
        const water = waterMap.get(x, z);
        return (useFilledWaterSurface ? ground + water : ground) * worldHeight;
    }

    private createGeometry(
        heightMap: Float32Matrix2D,
        waterMap: Float32Matrix2D,
        textureSize: number,
        useFilledWaterSurface: boolean,
    ): THREE.BufferGeometry {
        const positions: number[] = [];
        const uvs: number[] = [];
        const indices: number[] = [];
        const gridSize = heightMap.width;
        const fullTextureSize = textureSize + 2;
        const surfaceUV = (coordinate: number): number => (coordinate + 1.5) / fullTextureSize;
        const skirtUV = (coordinate: number): number => (coordinate + 0.5) / fullTextureSize;
        const addVertex = (x: number, y: number, z: number, u: number, v: number): number => {
            positions.push(x, y, z);
            uvs.push(u, v);
            return positions.length / 3 - 1;
        };

        for (let z = 0; z < gridSize; z++) {
            for (let x = 0; x < gridSize; x++) {
                addVertex(
                    x * chunkSize / (gridSize - 1),
                    this.getSurfaceElevation(heightMap, waterMap, x, z, useFilledWaterSurface),
                    z * chunkSize / (gridSize - 1),
                    surfaceUV(x),
                    surfaceUV(z),
                );
            }
        }
        for (let z = 0; z < gridSize - 1; z++) {
            for (let x = 0; x < gridSize - 1; x++) {
                const a = z * gridSize + x;
                const b = a + 1;
                const c = a + gridSize;
                const d = c + 1;
                indices.push(a, c, b, b, c, d);
            }
        }

        const addSkirt = (surface: number[], getUV: (index: number) => [number, number]): void => {
            const top = surface.map((index, i) => {
                const [u, v] = getUV(i);
                return addVertex(positions[index * 3], positions[index * 3 + 1], positions[index * 3 + 2], u, v);
            });
            const bottom = surface.map((index, i) => {
                const [u, v] = getUV(i);
                return addVertex(positions[index * 3], positions[index * 3 + 1] - skirtDepth, positions[index * 3 + 2], u, v);
            });
            for (let i = 0; i < surface.length - 1; i++) {
                indices.push(top[i], bottom[i], top[i + 1], top[i + 1], bottom[i], bottom[i + 1]);
            }
        };
        const north: number[] = [], south: number[] = [], west: number[] = [], east: number[] = [];
        for (let i = 0; i < gridSize; i++) {
            north.push(i);
            south.push((gridSize - 1) * gridSize + i);
            west.push(i * gridSize);
            east.push(i * gridSize + gridSize - 1);
        }
        addSkirt(north, i => [surfaceUV(i), skirtUV(0)]);
        addSkirt(south, i => [surfaceUV(i), skirtUV(textureSize + 1)]);
        addSkirt(west, i => [skirtUV(0), surfaceUV(i)]);
        addSkirt(east, i => [skirtUV(textureSize + 1), surfaceUV(i)]);

        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
        geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
        geometry.setIndex(indices);
        geometry.computeVertexNormals();
        geometry.computeBoundingSphere();
        return geometry;
    }

    private generateTerrainTexture(
        heightMap: Float32Matrix2D,
        waterMap: Float32Matrix2D,
        textureSize: number,
        useTextureWater: boolean,
    ): HTMLCanvasElement {
        const canvas = document.createElement('canvas');
        const fullTextureSize = textureSize + 2;
        canvas.width = fullTextureSize;
        canvas.height = fullTextureSize;
        const context = canvas.getContext('2d');
        if (!context) throw new Error('Context 2D not created');

        const image = context.createImageData(fullTextureSize, fullTextureSize);
        const rockColor = new THREE.Color(0x3a3232);
        const grassColor = new THREE.Color(0x41DD0a);
        const waterColor = new THREE.Color(this.generator.options.water.color);
        const snowColor = new THREE.Color(0xFFFFFF);
        const tempColor = new THREE.Color();
        const normal = new THREE.Vector3();
        const up = new THREE.Vector3(0, 1, 0);
        // A coarser texture has a larger world-space distance between adjacent
        // samples. Normalize the gradient step so the shading remains stable
        // when the texture resolution changes.
        const shadingScale = baseShadingScale
            * (textureSize - 1)
            / Math.max(terrainLODs[0].textureSize - 1, 1);

        for (let x = 0; x < fullTextureSize; x++) {
            for (let z = 0; z < fullTextureSize; z++) {
                const sourceX = clamp(x - 1, 0, textureSize - 1);
                const sourceZ = clamp(z - 1, 0, textureSize - 1);
                const mapX = Math.round(sourceX * (heightMap.width - 1) / Math.max(textureSize - 1, 1));
                const mapZ = Math.round(sourceZ * (heightMap.height - 1) / Math.max(textureSize - 1, 1));
                const index = (fullTextureSize * z + x) * 4;
                const ground = heightMap.get(mapX, mapZ);
                const water = waterMap.get(mapX, mapZ);
                const left = heightMap.get(Math.max(mapX - 1, 0), mapZ);
                const right = heightMap.get(Math.min(mapX + 1, heightMap.width - 1), mapZ);
                const top = heightMap.get(mapX, Math.max(mapZ - 1, 0));
                const bottom = heightMap.get(mapX, Math.min(mapZ + 1, heightMap.height - 1));
                normal.set((left - right) * shadingScale, 2, (top - bottom) * shadingScale).normalize();
                const shade = Math.abs(normal.dot(up));
                const color = useTextureWater && water > 0 ? waterColor : ground > this.generator.options.snowLevel + shade * 0.1 ? snowColor : tempColor.lerpColors(rockColor, grassColor, shade * shade * shade);
                image.data[index] = Math.round(color.r * 255);
                image.data[index + 1] = Math.round(color.g * 255);
                image.data[index + 2] = Math.round(color.b * 255);
                image.data[index + 3] = 255;
            }
        }
        context.putImageData(image, 0, 0);
        return canvas;
    }

    private createWaterResources(waterMap: Float32Matrix2D, gridSize: number): {
        geometry: THREE.BufferGeometry;
        material: THREE.ShaderMaterial;
    } {
        const geometry = new THREE.PlaneGeometry(chunkSize, chunkSize, gridSize - 1, gridSize - 1);
        geometry.rotateX(-Math.PI / 2);
        geometry.translate(
            0,
            this.generator.options.waterLevel * worldHeight + 0.05,
            0,
        );

        const positions = geometry.getAttribute('position');
        const maskValues = new Float32Array(positions.count);
        for (let i = 0; i < positions.count; i++) {
            const x = clamp(Math.round((positions.getX(i) + chunkSize / 2) * (gridSize - 1) / chunkSize), 0, gridSize - 1);
            const z = clamp(Math.round((positions.getZ(i) + chunkSize / 2) * (gridSize - 1) / chunkSize), 0, gridSize - 1);
            // The water body is defined by the waterline, not by water depth.
            // Every point below waterLevel must receive the same solid mask.
            maskValues[i] = waterMap.get(x, z) > 0 ? 1 : 0;
        }
        geometry.setAttribute('waterMask', new THREE.Float32BufferAttribute(maskValues, 1));

        const water = this.generator.options.water;
        const material = new THREE.ShaderMaterial({
            vertexShader: waterVertexShader,
            fragmentShader: waterFragmentShader,
            uniforms: {
                uWaterColor: { value: new THREE.Color(water.color) },
                uTime: { value: 0 },
                uWaveSpeed: { value: water.waveSpeed },
                uWaveScale: { value: water.waveScale },
                uOpacity: { value: water.opacity },
            },
            transparent: true,
            depthWrite: true,
            side: THREE.DoubleSide,
        });

        return { geometry, material };
    }

    private updateWaterUniforms(elapsedTime: number): void {
        if (this.waterMaterial === null) return;
        this.waterMaterial.uniforms.uTime.value = elapsedTime;
    }
}
